//src/app/services/fincenReports/index.ts
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedRepsonse,
} from '../index';
import { IUser } from '../user';

export interface IFincenReport {
  id: number;
  year: number;
  type: 'business' | 'individual';
  status: 'in_progress' | 'completed' | 'submitted' | 'cancelled';
  payment_status: 'unpaid' | 'paid'; // Add this field
  title: string;
  creator: IUser;
  business_id?: number;
  business?: { id: number; name: string };
  created_at: string;
  report_price?: number;
  data?: IFincenReportForm;
  external_report_id?: string; // New field
}

export interface IFincenReportForm {
  type: 'individual' | 'business';
  business_id?: number;

  year: number;
  submittedbyfirstname: string;
  submittedbylastname: string;
  ssn: string;
  phoneNumber: string;
  submittedbyemail: string;

  mailingaddress: string;
  apartment: string;
  mailingcity: string;
  state: string;
  zip: string;
  liveOutsideOfUs: boolean;
  otherCountry: string;
  provinceCounty: string;
  businessType?: string;

  notes: string;
  reachPhoneNumber: string;
  whatIsTheBestTimeToReachYouAtThisNumber: string;
  agreement: boolean;

  latestPaySub: {
    files: {
      file_url: string;
      title: string;
    };
  }[];

  priorYearTaxReturn: {
    files: {
      file_url: string;
      title: string;
    };
  }[];
}

export const FincenReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getFincenReports: builder.query<
      IPaginatedRepsonse<IFincenReport>,
      IGetQueryParams<{
        status?: IFincenReport['status'];
        client_id?: number;
        business_id?: number;
      }>
    >({
      query: (params) => 'fincen_reports_requests' + buildQueryParams(params),
    }),

    getFincenReportById: builder.query<IFincenReport, number>({
      query: (id) => `/fincen_reports_requests/${id}`,
      transformResponse: (response: { request: IFincenReport }) =>
        response.request,
    }),

    createFincenReport: builder.mutation<
      Partial<IFincenReport>,
      Partial<IFincenReport>
    >({
      query: (request) => {
        return {
          url: `/fincen_reports_requests`,
          method: 'POST',
          body: { request },
        };
      },
      transformResponse: (response: { request: IFincenReport }) =>
        response.request,
    }),

    updateFincenReport: builder.mutation<IFincenReport, Partial<IFincenReport>>(
      {
        query: (request: Partial<IFincenReport>) => {
          const body = {
            request,
          };
          return {
            url: `/fincen_reports_requests/${body.request.id}`,
            method: 'PUT',
            body,
          };
        },

        transformResponse: (response: { request: IFincenReport }) =>
          response.request,

        onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
          return queryFulfilled.then((response) => {
            dispatch(
              FincenReportApi.util.updateQueryData(
                'getFincenReportById',
                id!,
                (draft) => Object.assign(draft, response.data),
              ),
            );
          });
        },
      },
    ),

    updateFincenReportStatus: builder.mutation<
      IFincenReport,
      { id: number; status: IFincenReport['status'] }
    >({
      query: ({ id, status }) => {
        let route = '';
        if (status === 'in_progress') route = 'set_as_in_progress';
        if (status === 'completed') route = 'complete';
        if (status === 'cancelled') route = 'cancel';

        return {
          url: `/fincen_reports_requests/${id}/${route}`,
          method: 'POST',
        };
      },

      transformResponse: (response: { request: IFincenReport }) =>
        response.request,

      onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => {
        return queryFulfilled.then((response) => {
          dispatch(
            FincenReportApi.util.updateQueryData(
              'getFincenReportById',
              id!,
              (draft) => Object.assign(draft, response.data),
            ),
          );
        });
      },
    }),

    deleteFincenReport: builder.mutation<IFincenReport, number>({
      query: (id) => {
        return {
          url: `/fincen_reports_requests/${id}`,
          method: 'Delete',
        };
      },

      transformResponse: (response: { task: IFincenReport }) => response.task,
    }),

    payFincenReport: builder.mutation<{ checkout_url: string }, number>({
      query: (requestId) => ({
        url: `/fincen_reports_requests/${requestId}/pay`,
        method: 'POST',
      }),
      transformResponse: (response: { checkout_url: string }) => response,
    }),
  }),
});

export const {
  useUpdateFincenReportStatusMutation,
  useLazyGetFincenReportsQuery,
  useUpdateFincenReportMutation,
  useGetFincenReportByIdQuery,
  useDeleteFincenReportMutation,
  useCreateFincenReportMutation,
  usePayFincenReportMutation, // Add this export
} = FincenReportApi;
